import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { beginGlobalNetworkRequest, endGlobalNetworkRequest } from './globalNetworkLoader';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setTokens,
} from './authTokenStorage';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const stopTrackedLoader = (config) => {
  if (!config || !config._globalLoaderTracked) return;
  config._globalLoaderTracked = false;
  endGlobalNetworkRequest();
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    if (!config._skipGlobalLoader) {
      beginGlobalNetworkRequest();
      config._globalLoaderTracked = true;
    }
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Cache-busting parameter for GET requests instead of headers (to avoid CORS issues).
    // Skip when the caller explicitly opts out — e.g. `/accounts/me/` and other endpoints
    // that change rarely, so the browser can serve them from cache between rapid re-fetches
    // (tab focus, route mount, profile sidebar, etc.).
    if (config.method === 'get' && !config._skipCacheBust) {
      if (!config.params) {
        config.params = {};
      }
      config.params._t = Date.now();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh
api.interceptors.response.use(
  (response) => {
    stopTrackedLoader(response.config);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    stopTrackedLoader(originalRequest);

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw new Error('Missing refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/accounts/token/refresh/`, {
          refresh: refreshToken
        });

        const { access, refresh: rotatedRefresh } = response.data;
        if (rotatedRefresh) {
          setTokens({ access, refresh: rotatedRefresh });
        } else {
          setAccessToken(access);
        }

        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearTokens();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Extract the path and query string from a pagination 'next' URL
 * Handles both full URLs and relative paths
 * Removes /api prefix if present since axios baseURL already includes it
 * @param {string} nextUrl - The next URL from pagination response
 * @returns {string|null} - The path with query string, or null if invalid
 */
export const extractNextUrlPath = (nextUrl) => {
  if (!nextUrl) return null;
  
  let path = null;
  
  // If it's already a relative path (starts with /), use it directly
  if (nextUrl.startsWith('/')) {
    path = nextUrl;
  } else {
    // If it's a full URL, extract the path and query string
    try {
      const url = new URL(nextUrl);
      path = url.pathname + url.search;
    } catch (e) {
      // If URL parsing fails, return as-is (might be a relative path without leading slash)
      path = nextUrl;
    }
  }
  
  // Remove /api prefix if present since axios baseURL already includes /api
  // This prevents /api/api/ duplication
  if (path && path.startsWith('/api/')) {
    path = path.substring(4); // Remove '/api' (4 characters)
  }
  
  return path;
};

export default api;

